import api from './api';

export interface ProductoDepositoDto {
  nroUbicacion: number;
  nombreUbicacion: string;
  cantidad: number;
  cantidadAsignada: number;
  ubicacion1: string | null;
  ubicacion2: string | null;
  ubicacion3: string | null;
  ubicacion4: string | null;
}

export interface ProductoDepositosLabels {
  ubicacion1: string;
  ubicacion2: string;
  ubicacion3: string;
  ubicacion4: string;
}

export interface ProductoDepositosResponse {
  usaEstanterias: boolean;
  labels: ProductoDepositosLabels;
  depositos: ProductoDepositoDto[];
}

export interface ReplaceProductoDepositoItem {
  nroUbicacion: number;
  ubicacion1: string | null;
  ubicacion2: string | null;
  ubicacion3: string | null;
  ubicacion4: string | null;
}

export interface ReplaceProductoDepositosDto {
  depositos: ReplaceProductoDepositoItem[];
}

function codigoNormalizado(codigoArticulo: string | null | undefined): string {
  return (codigoArticulo || '').trim();
}

function codigoParaApi(codigoArticulo: string | null | undefined): string {
  return codigoNormalizado(codigoArticulo) || 'nuevo';
}

function textoUbicacion(value: unknown): string | null {
  if (value == null) return null;
  const texto = String(value).trim();
  return texto || null;
}

function pick(raw: Record<string, unknown> | null | undefined, ...keys: string[]): unknown {
  if (!raw) return undefined;
  const lower = new Map(Object.keys(raw).map((k) => [k.toLowerCase(), k]));
  for (const key of keys) {
    const real = lower.get(key.toLowerCase());
    if (!real) continue;
    const value = raw[real];
    if (value !== undefined) return value;
  }
  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function numeroOCero(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function labelsDesdeApi(raw?: unknown): ProductoDepositosLabels {
  const obj = asRecord(raw);
  const texto = (key: string, fallback: string) => {
    const value = textoUbicacion(pick(obj ?? undefined, key));
    return value || fallback;
  };
  return {
    ubicacion1: texto('ubicacion1', 'Ubicación 1'),
    ubicacion2: texto('ubicacion2', 'Ubicación 2'),
    ubicacion3: texto('ubicacion3', 'Ubicación 3'),
    ubicacion4: texto('ubicacion4', 'Ubicación 4'),
  };
}

function mapDeposito(raw: unknown): ProductoDepositoDto | null {
  const obj = asRecord(raw);
  const nro = Number(pick(obj ?? undefined, 'nroUbicacion'));
  if (!Number.isFinite(nro)) return null;
  return {
    nroUbicacion: nro,
    nombreUbicacion: String(pick(obj ?? undefined, 'nombreUbicacion') ?? '').trim() || `Depósito ${nro}`,
    cantidad: numeroOCero(pick(obj ?? undefined, 'cantidad')),
    cantidadAsignada: numeroOCero(pick(obj ?? undefined, 'cantidadAsignada')),
    ubicacion1: textoUbicacion(pick(obj ?? undefined, 'ubicacion1')),
    ubicacion2: textoUbicacion(pick(obj ?? undefined, 'ubicacion2')),
    ubicacion3: textoUbicacion(pick(obj ?? undefined, 'ubicacion3')),
    ubicacion4: textoUbicacion(pick(obj ?? undefined, 'ubicacion4')),
  };
}

/** Solo true si la API lo envía explícitamente. */
function parseUsaEstanterias(raw: unknown): boolean {
  return raw === true || raw === 1 || raw === '1' || raw === 'true' || raw === 'S';
}

function parseDepositosResponse(data: unknown): ProductoDepositosResponse {
  const root = asRecord(data);
  const listaRaw = Array.isArray(data) ? data : pick(root ?? undefined, 'depositos');
  const depositos = (Array.isArray(listaRaw) ? listaRaw : [])
    .map(mapDeposito)
    .filter((d): d is ProductoDepositoDto => d != null);
  return {
    usaEstanterias: parseUsaEstanterias(pick(root ?? undefined, 'usaEstanterias')),
    labels: labelsDesdeApi(pick(root ?? undefined, 'labels')),
    depositos,
  };
}

const DEPOSITOS_PATH = (codigo: string) =>
  `/api/productos/${encodeURIComponent(codigo)}/depositos`;

function toReplaceItems(
  depositos: ReplaceProductoDepositoItem[] | ReplaceProductoDepositosDto,
): ReplaceProductoDepositoItem[] {
  const lista = Array.isArray(depositos) ? depositos : depositos.depositos;
  return (lista || []).map((d) => ({
    nroUbicacion: d.nroUbicacion,
    ubicacion1: textoUbicacion(d.ubicacion1),
    ubicacion2: textoUbicacion(d.ubicacion2),
    ubicacion3: textoUbicacion(d.ubicacion3),
    ubicacion4: textoUbicacion(d.ubicacion4),
  }));
}

const depositosApiCache = new Map<string, ProductoDepositosResponse>();

export function claveProductoDeposito(codigoArticulo: string, nroUbicacion: number): string {
  return `${codigoNormalizado(codigoArticulo).toUpperCase()}|${nroUbicacion}`;
}

export function partesUbicacionFisica(
  row: Partial<Pick<ProductoDepositoDto, 'ubicacion1' | 'ubicacion2' | 'ubicacion3' | 'ubicacion4'>>,
  labels?: Partial<ProductoDepositosLabels> | null,
): Array<{ label: string; value: string }> {
  const nombres = labelsDesdeApi(labels);
  return (
    [
      [nombres.ubicacion1, row.ubicacion1],
      [nombres.ubicacion2, row.ubicacion2],
      [nombres.ubicacion3, row.ubicacion3],
      [nombres.ubicacion4, row.ubicacion4],
    ] as Array<[string, string | null | undefined]>
  )
    .filter(([, value]) => value != null && String(value).trim() !== '')
    .map(([label, value]) => ({ label, value: String(value).trim() }));
}

export function formatoUbicacionFisica(
  row: Partial<Pick<ProductoDepositoDto, 'ubicacion1' | 'ubicacion2' | 'ubicacion3' | 'ubicacion4'>>,
  labels?: Partial<ProductoDepositosLabels> | null,
): string {
  return partesUbicacionFisica(row, labels)
    .map(({ label, value }) => `${label}: ${value}`)
    .join(' · ');
}

async function getDepositos(
  codigoArticulo: string,
  options?: { useCache?: boolean },
): Promise<ProductoDepositosResponse> {
  const codigo = codigoParaApi(codigoArticulo);
  if (options?.useCache) {
    const cached = depositosApiCache.get(codigo);
    if (cached) return cached;
  }
  const response = await api.get<unknown>(DEPOSITOS_PATH(codigo));
  const result = parseDepositosResponse(response.data);
  depositosApiCache.set(codigo, result);
  return result;
}

async function replaceDepositos(
  codigoArticulo: string,
  depositos: ReplaceProductoDepositoItem[] | ReplaceProductoDepositosDto,
): Promise<void> {
  const items = toReplaceItems(depositos);
  const codigo = codigoNormalizado(codigoArticulo);
  if (!codigo) return;

  await api.put(DEPOSITOS_PATH(codigo), { depositos: items });
  depositosApiCache.delete(codigo);
}

/** GET de códigos únicos de la página, con cache en memoria para evitar N+1. */
async function resolverUbicacionesFisicas(
  filas: Array<{ codigoArticulo: string; nroUbicacion: number }>,
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  if (!filas.length) return result;

  const unicos = Array.from(
    new Set(filas.map((f) => codigoNormalizado(f.codigoArticulo)).filter(Boolean)),
  );
  const porCodigo = new Map<string, ProductoDepositosResponse>();
  await Promise.all(
    unicos.map(async (codigo) => {
      try {
        porCodigo.set(codigo, await getDepositos(codigo, { useCache: true }));
      } catch {
        /* sin ubicación para este artículo */
      }
    }),
  );

  for (const fila of filas) {
    const codigo = codigoNormalizado(fila.codigoArticulo);
    const res = porCodigo.get(codigo);
    if (!res?.usaEstanterias) continue;
    const dep = res.depositos.find((d) => d.nroUbicacion === fila.nroUbicacion);
    if (!dep) continue;
    const texto = formatoUbicacionFisica(dep, res.labels);
    if (texto) {
      result.set(claveProductoDeposito(codigo, fila.nroUbicacion), texto);
    }
  }

  return result;
}

const productoDepositoService = {
  getDepositos,
  replaceDepositos,
  resolverUbicacionesFisicas,
};

export default productoDepositoService;
